import React, { useEffect, useState, useCallback } from "react";
import { getModuleParams } from "../api/parameters";
import {
  userId,
  userClientDb,
  uiLanguage,
  userDepartmentId,
} from "../auth/selectors/userSelector";
import { useAppDispatch, useAppSelector } from "../../app/hook";
import { setUserDepartmentId } from "../auth/slices/userSlice";
import { useLocalStorage, useAuthorizationError, useLoader } from "../../hooks";
import { ItemsList, Alert } from "../../components";
import { useTranslation } from "react-i18next";
import { getErrorMessage } from "../../components/form/formUtils";
import { usePreviousState } from "../../hooks";
import { LoginLayout } from "../../layouts";
import { Navigate } from "react-router-dom";
import * as Routes from "../../app/routes";
import DepLogo from "../../logo/storage-light1.svg";
import DepLogoDark from "../../logo/storage-dark1.svg";
import { useColorScheme } from "@mui/material/styles";

function DepartmentList() {
  const [translate] = useTranslation();
  const user = useAppSelector(userId);
  const userDb = useAppSelector(userClientDb);
  const userDepartment = useAppSelector(userDepartmentId);
  const [logoutUser] = useAuthorizationError();
  const dispatch = useAppDispatch();
  const [data, setData] = useState<Array<{ value: string; label: string }>>();
  const [localStorage, setLocalStorage] = useLocalStorage("departmentId");
  const [error, setError] = useState({ error: false, message: "", status: 0 });
  const [, setLoader] = useLoader();
  const locale = useAppSelector(uiLanguage);
  const previousDb = usePreviousState(userDb ? userDb.id : 0);
  const [redirect, setRedirect] = useState(false);
  const { mode } = useColorScheme();

  const params = {
    clientDbId: userDb ? userDb.id : 0,
    locale: locale,
    module: "",
  };

  const assignDepId = React.useCallback(
    (selectedValue: string) => {
      const isUser =
        localStorage && Array.isArray(localStorage)
          ? localStorage.filter(
              (person: any) =>
                person.userId === user && userDb && person.dbId === userDb.id
            )
          : [];

      if (isUser.length === 0) {
        if (localStorage && localStorage.length > 0) {
          const newStorage = [
            ...localStorage,
            { departmentId: selectedValue, userId: user, dbId: userDb?.id },
          ];
          setLocalStorage(newStorage);
        } else {
          setLocalStorage([
            { departmentId: selectedValue, userId: user, dbId: userDb?.id },
          ]);
        }

        dispatch(setUserDepartmentId(selectedValue));
      } else {
        dispatch(setUserDepartmentId(selectedValue));

        const newStorage = localStorage.filter(
          (person: any) =>
            !(person.userId === user && person.dbId === userDb?.id)
        );
        setLocalStorage([
          ...newStorage,
          { departmentId: selectedValue, userId: user, dbId: userDb?.id },
        ]);
      }

      setRedirect(true);
    }, // eslint-disable-next-line
    [dispatch, data, setLocalStorage, userDb]
  );

  const handleItemClick = (selectedValue: string) => {
    assignDepId(selectedValue);
  };

  useEffect(
    () => {
      if (user && userDb && previousDb !== userDb.id) {
        setLoader(true);

        getModuleParams(params)
          .then(({ data }) => {
            setLoader(false);

            const departmentsArray = data.filter(
              (dep: any) =>
                dep.parameter && dep.parameter.toUpperCase() === "DEPARTMENTS"
            );

            // Jeigu parametras neaprasytas iskart nukreipiama i sekanti langa
            if (departmentsArray.length === 0) {
              assignDepId("");
              setRedirect(true);
              return;
            }

            if (departmentsArray && departmentsArray.length > 0) {
              setData(
                departmentsArray.map((dep: any) => ({
                  value: "" + dep.code,
                  label: "" + dep.name,
                }))
              );
            }
          })
          .catch((reason) => {
            setLoader(false);
            const errMessage = getErrorMessage(reason);
            setError({
              error: true,
              message: errMessage.message,
              status: errMessage.status,
            });
          });
      }
    }, // eslint-disable-next-line
    [userDb, translate, userDepartment]
  );

  useEffect(() => {
    const chosen = data ? data.filter((dp) => dp.value === userDepartment) : [];

    if (chosen.length > 0) assignDepId(chosen[0].value);
  }, [data, userDepartment, assignDepId]);

  const handleErrorClose = () => {
    logoutUser(error.status);

    if (error.status !== 401)
      setError({ error: false, message: "", status: 0 });
  };

  const handleSkip = useCallback(() => {
    setRedirect(true);
  }, []);

  return (
    <LoginLayout>
      {error && (
        <Alert
          isOpen={error.error}
          message={error.message}
          iconColor="error"
          onClose={handleErrorClose}
        />
      )}

      {data && (
        <ItemsList
          title={translate("selectDepartment", "Pasirinkite padalinį")}
          data={data}
          actionBtnTitle={translate("skip", "Praleisti nepasirinkus")}
          actionBtnClick={handleSkip}
          onItemClick={handleItemClick}
          logoSrc={mode === "dark" ? DepLogoDark : DepLogo}
        />
      )}

      {redirect && <Navigate to={Routes.ROUTE_OPTIONS.path} />}
    </LoginLayout>
  );
}

export default DepartmentList;
